/**
 * First-touch attribution: where a visitor came from the very first time they
 * reached StaxED, captured automatically so we don't rely on the onboarding
 * answer alone.
 *
 * Stored in a cookie on `.staxed.in` so the landing site (staxed.in, which
 * writes the same cookie -- see landing/src/lib/attribution.ts) and the app
 * (app.staxed.in) share it. Whoever sees the visitor first wins; later visits
 * never overwrite it. The value is sent once, with the onboarding save, and
 * the backend keeps it write-once on the user row.
 *
 * AI assistants show up here as a referrer (chatgpt.com, perplexity.ai,
 * gemini.google.com, ...) or as utm_source=chatgpt.com, which ChatGPT appends
 * to links it cites.
 */

export type FirstTouch = {
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_path?: string;
  first_seen_at?: string;
};

const COOKIE = "stx_ft";
const MAX_AGE_S = 180 * 86400;

function cookieDomain(): string {
  return location.hostname.endsWith("staxed.in") ? "; domain=.staxed.in" : "";
}

export function readFirstTouch(): FirstTouch | null {
  try {
    const raw = document.cookie
      .split("; ")
      .find((c) => c.startsWith(`${COOKIE}=`))
      ?.slice(COOKIE.length + 1);
    return raw ? (JSON.parse(decodeURIComponent(raw)) as FirstTouch) : null;
  } catch {
    return null;
  }
}

/** Record this visit as the first touch, unless one is already recorded. */
export function captureFirstTouch(): void {
  try {
    if (readFirstTouch()) return;
    const params = new URLSearchParams(location.search);
    const touch: FirstTouch = { landing_path: location.pathname, first_seen_at: new Date().toISOString() };
    // Origin + path only: referrer query strings can carry other sites' data.
    if (document.referrer) {
      const ref = new URL(document.referrer);
      if (!ref.hostname.endsWith("staxed.in")) touch.referrer = ref.origin + ref.pathname;
    }
    for (const k of ["utm_source", "utm_medium", "utm_campaign"] as const) {
      const v = params.get(k);
      if (v) touch[k] = v.slice(0, 100);
    }
    const value = encodeURIComponent(JSON.stringify(touch));
    document.cookie = `${COOKIE}=${value}; path=/; max-age=${MAX_AGE_S}; SameSite=Lax${cookieDomain()}`;
  } catch {
    /* attribution is best-effort; never break page load */
  }
}
